/**
 * <fitment-finder> — the cascading Type > Make > Year > Model selector.
 *
 * Reads the taxonomy through window.FitmentData and, on a complete selection,
 * saves the machine to window.Garage and sends the customer to that vehicle's
 * collection. The collection handle is derived from the selection, so this needs
 * no backend — see docs/fitment-api-spec.md §1.
 */
const FITMENT_TIERS = ['type', 'make', 'year', 'model'];

class FitmentFinder extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;

    this.selects = {};
    for (const tier of FITMENT_TIERS) {
      const el = this.querySelector(`[data-tier-select="${tier}"]`);
      if (!el) continue;
      this.selects[tier] = el;
      el.addEventListener('change', () => this.onChange(tier));
    }

    this.submit = this.querySelector('[data-fitment-submit]');
    this.status = this.querySelector('[data-fitment-status]');
    this.autoNavigate = this.dataset.autoNavigate === 'true';

    this.submit?.addEventListener('click', () => this.go());

    this.setStatus('Loading vehicles…');

    window.FitmentData.load(this.dataset.taxonomyUrl)
      .then(() => {
        this.setStatus('');
        this.fill('type');
        // A brand page narrows the finder to its make; a family tile narrows
        // it further. Either wins over the garage's current machine.
        if (this.dataset.presetMake || this.dataset.presetType) {
          this.preset({
            type: this.dataset.presetType,
            make: this.dataset.presetMake,
            modelPrefix: this.dataset.presetModelPrefix,
          });
        } else {
          this.prefillFromGarage();
        }
      })
      .catch((err) => {
        this.setStatus('Vehicle list unavailable. Please refresh to try again.', 'error');
        console.error('[fitment-finder]', err);
      });

    // A taxonomy refresh arriving mid-session shouldn't strand a stale dropdown.
    document.addEventListener('fitment:taxonomy-updated', () => {
      const previous = this.selection();
      this.fill('type');
      for (const tier of FITMENT_TIERS) {
        if (!previous[tier] || !this.selects[tier]) continue;
        this.selects[tier].value = previous[tier];
        this.onChange(tier, { silent: true });
      }
    });
  }

  /** Current value of every tier. */
  selection() {
    const sel = {};
    for (const tier of FITMENT_TIERS) {
      sel[tier] = this.selects[tier]?.value ?? '';
    }
    return sel;
  }

  placeholderFor(select, tier) {
    const option = document.createElement('option');
    option.value = '';
    option.textContent = `Select ${select.dataset.tierLabel || tier}`;
    return option;
  }

  fill(tier) {
    const select = this.selects[tier];
    if (!select) return;

    let values = window.FitmentData.options(tier, this.selection());

    // Presets narrow the lists: only the types that carry the preset make,
    // only the models in the preset family. Nothing is hidden that could
    // still resolve to a vehicle of that make.
    if (tier === 'type' && this.presetMake) {
      values = values.filter((type) => window.FitmentData.options('make', { type }).includes(this.presetMake));
    }
    if (tier === 'model' && this.modelPrefix) {
      const prefix = this.modelPrefix.toLowerCase();
      const narrowed = values.filter((m) => m.toLowerCase().startsWith(prefix));
      if (narrowed.length) values = narrowed;
    }

    // Rebuild off-DOM so a long model list costs one reflow, not hundreds.
    const frag = document.createDocumentFragment();
    frag.appendChild(this.placeholderFor(select, tier));

    for (const value of values) {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      frag.appendChild(option);
    }

    select.replaceChildren(frag);
    select.disabled = values.length === 0;
  }

  /** Clear and disable every tier from `fromIndex` down. */
  reset(fromIndex) {
    for (const tier of FITMENT_TIERS.slice(fromIndex)) {
      const select = this.selects[tier];
      if (!select) continue;
      select.replaceChildren(this.placeholderFor(select, tier));
      select.disabled = true;
    }
  }

  onChange(tier, { silent = false } = {}) {
    const index = FITMENT_TIERS.indexOf(tier);

    this.reset(index + 1);
    if (this.selects[tier]?.value && index + 1 < FITMENT_TIERS.length) {
      this.fill(FITMENT_TIERS[index + 1]);
    }

    // With a make preset, choosing the type picks the make and moves on to
    // the year, so the customer only answers what the page doesn't know.
    if (tier === 'type' && this.presetMake && this.selects.make) {
      const makes = [...this.selects.make.options].map((o) => o.value);
      if (makes.includes(this.presetMake)) {
        this.selects.make.value = this.presetMake;
        this.reset(2);
        this.fill('year');
      }
    }

    const vehicle = window.FitmentData.resolve(this.selection());
    if (this.submit) this.submit.disabled = !vehicle;
    if (silent) return;

    if (!vehicle) {
      this.setStatus('');
    } else if (this.autoNavigate) {
      this.go();
    } else {
      this.setStatus(`${vehicle.label} selected.`);
    }
  }

  /**
   * Save to the garage, then navigate to that vehicle's collection.
   *
   * The navigation is announced first as a cancelable `fitment:selected`, so
   * a host that wants to stay put can cancel it. The garage modal no longer
   * does (Lucas, 2026-10-13): choosing a machine there is a search too.
   */
  go() {
    const vehicle = window.FitmentData.resolve(this.selection());
    if (!vehicle) return;

    window.Garage?.add(vehicle);

    const proceed = this.dispatchEvent(new CustomEvent('fitment:selected', {
      detail: { vehicle },
      bubbles: true,
      cancelable: true,
    }));

    if (proceed) window.location.href = vehicle.url;
  }

  /**
   * Narrow the finder: to a make (a brand page), a type, a model family.
   * Resets the cascade first, so a preset always starts clean, then fills as
   * far as the preset allows — with one possible type the customer lands
   * straight on the year.
   */
  preset({ type, make, modelPrefix } = {}) {
    this.presetMake = make || null;
    this.modelPrefix = modelPrefix || null;
    this.reset(0);
    this.fill('type');

    const types = [...(this.selects.type?.options ?? [])].map((o) => o.value).filter(Boolean);
    const chosenType = type && types.includes(type) ? type : (types.length === 1 ? types[0] : null);
    if (chosenType && this.selects.type) {
      this.selects.type.value = chosenType;
      this.onChange('type', { silent: true });
    }
    if (this.submit) this.submit.disabled = true;
    this.setStatus('');
  }

  /** Preselect the garage's active machine so the finder reflects current state. */
  prefillFromGarage() {
    const current = window.Garage?.active();
    if (!current) return;

    const vehicle = window.FitmentData.fromKey(current.key);
    if (!vehicle) return; // vehicle was retired from the taxonomy

    FITMENT_TIERS.forEach((tier, i) => {
      const select = this.selects[tier];
      if (!select) return;
      if (i > 0) this.fill(tier); // each tier's options depend on the one above
      select.value = String(vehicle[tier]);
    });

    if (this.submit) this.submit.disabled = false;
  }

  setStatus(message, state) {
    if (!this.status) return;
    this.status.textContent = message || '';
    if (state) {
      this.status.setAttribute('data-state', state);
    } else {
      this.status.removeAttribute('data-state');
    }
  }
}

if (!customElements.get('fitment-finder')) {
  customElements.define('fitment-finder', FitmentFinder);
}
